import React, { useState } from 'react';
import { useQuery } from 'react-query';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Chip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Work as WorkIcon,
  Visibility as VisibilityIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { format } from 'date-fns';

// Use env var if set, otherwise use relative URL
const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  if (typeof window !== 'undefined' && (window.location.hostname.includes('ondigitalocean.app') || window.location.hostname.includes('services.knls.in'))) {
    return '/api';
  }
  return 'http://localhost:3000/api';
};

const API_BASE_URL = getApiBaseUrl();

const getAuthHeaders = () => {
  const token = localStorage.getItem('token') || localStorage.getItem('admin_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const categories = [
  { value: 'ssc', label: 'SSC' },
  { value: 'railway', label: 'Railway' },
  { value: 'bank', label: 'Bank' },
  { value: 'teaching', label: 'Teaching' },
  { value: 'police', label: 'Police' },
  { value: 'other', label: 'Other' },
];

const JobAlertsView = () => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [activeFilter, setActiveFilter] = useState('all');

  const { data, isLoading, error } = useQuery(
    ['job-alerts-view', page, search, categoryFilter, activeFilter],
    async () => {
      const params = {
        limit: 10,
        offset: (page - 1) * 10,
        search,
        category: categoryFilter !== 'all' ? categoryFilter : undefined,
        isActive: activeFilter === 'all' ? 'any' : activeFilter === 'active' ? 'true' : 'false',
      };
      // Remove undefined params
      Object.keys(params).forEach(key => params[key] === undefined && delete params[key]);
      
      const response = await axios.get(`${API_BASE_URL}/jobalerts`, {
        params,
        headers: getAuthHeaders(),
      });
      return response.data;
    },
    {
      refetchOnWindowFocus: true,
      refetchInterval: 30000,
    }
  );

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    try {
      return format(new Date(dateString), 'dd MMM yyyy');
    } catch {
      return '—';
    }
  };

  const getDaysRemaining = (lastDate) => {
    if (!lastDate) return null;
    try {
      const today = new Date();
      const endDate = new Date(lastDate);
      const diffTime = endDate - today;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    } catch {
      return null;
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Failed to load job alerts. Please try again.
        </Alert>
      </Box>
    );
  }

  const jobAlerts = data?.data || [];
  const totalPages = Math.ceil((data?.total || 0) / 10);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <WorkIcon /> Job Alerts View
      </Typography>

      {/* Filters */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            placeholder="Search job alerts..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <FormControl fullWidth>
            <InputLabel>Category</InputLabel>
            <Select
              value={categoryFilter}
              label="Category"
              onChange={(e) => {
                setCategoryFilter(e.target.value);
                setPage(1);
              }}
            >
              <MenuItem value="all">All Categories</MenuItem>
              {categories.map((cat) => (
                <MenuItem key={cat.value} value={cat.value}>
                  {cat.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={3}>
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              value={activeFilter}
              label="Status"
              onChange={(e) => {
                setActiveFilter(e.target.value);
                setPage(1);
              }}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {/* Job Alerts Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>Notification Name</strong></TableCell>
              <TableCell><strong>Organization</strong></TableCell>
              <TableCell><strong>Category</strong></TableCell>
              <TableCell><strong>Vacancies</strong></TableCell>
              <TableCell><strong>Last Date</strong></TableCell>
              <TableCell><strong>Days Left</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {jobAlerts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No job alerts found</Typography>
                </TableCell>
              </TableRow>
            ) : (
              jobAlerts.map((alert) => {
                const daysRemaining = getDaysRemaining(alert.lastDate);
                return (
                  <TableRow key={alert.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {alert.notificationName || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>{alert.organization || '—'}</TableCell>
                    <TableCell>
                      <Chip
                        label={alert.category || '—'}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{alert.numberOfVacancies || 0}</TableCell>
                    <TableCell>{formatDate(alert.lastDate)}</TableCell>
                    <TableCell>
                      {daysRemaining !== null ? (
                        <Chip
                          label={`${daysRemaining}d`}
                          size="small"
                          color={daysRemaining <= 7 ? 'error' : daysRemaining <= 30 ? 'warning' : 'success'}
                        />
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={alert.isActive ? 'Active' : 'Inactive'}
                        size="small"
                        color={alert.isActive ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      {alert.notificationUrl && (
                        <Tooltip title="Open notification">
                          <IconButton
                            size="small"
                            onClick={() => window.open(alert.notificationUrl, '_blank')}
                          >
                            <OpenInNewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(e, value) => setPage(value)}
            color="primary"
          />
        </Box>
      )}
    </Box>
  );
};

export default JobAlertsView;
